import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle2, ChevronRight, Mail, Globe } from 'lucide-react';
import Footer from '../components/karma/Footer';

const sections = [
  { id: 'section-1', num: '1', title: 'Who Are We?' },
  { id: 'section-2', num: '2', title: 'The Information on the Site' },
  { id: 'section-3', num: '3', title: 'Links to Other Sites' },
  { id: 'section-4', num: '4', title: 'Use of the Site' },
  { id: 'section-5', num: '5', title: 'Our Liability to You' },
  { id: 'section-6', num: '6', title: 'Your Personal Data' },
  { id: 'section-7', num: '7', title: 'Changes to the Terms' },
  { id: 'section-8', num: '8', title: 'Purchases and Bookings' },
  { id: 'section-9', num: '9', title: 'Invalidity' },
  { id: 'section-10', num: '10', title: 'Governing Law' },
  { id: 'section-11', num: '11', title: 'Contact Us' },
];

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState('section-1');

  useEffect(() => {
    document.title = 'Terms & Conditions — Karma Experience';
    window.scrollTo(0, 0);

    const handleScroll = () => {
      const scrollPos = window.scrollY + 180;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 110;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-[#FBF7EE] min-h-screen text-[#4a5568]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0F2340] border-b border-[#B8934A]/20 shadow-md">
        <div className="max-w-[1240px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/logo/KE-white.png"
              alt="Karma Experience"
              className="h-9 w-auto object-contain"
            />
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/activate"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#B8934A] hover:text-[#C9A75E] border border-[#B8934A]/40 hover:border-[#B8934A] rounded-full px-4 py-2 transition-all"
            >
              <ArrowLeft size={14} />
              <span>Back to Activation</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-[#0F2340] to-[#142A4A] text-white py-14 border-b border-[#B8934A]/20">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#B8934A]/15 border border-[#B8934A]/30 text-[#B8934A] text-xs uppercase tracking-[0.14em] font-semibold mb-4">
            <Shield size={13} />
            <span>Legal Documentation</span>
          </div>
          <h1 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl font-normal text-[#FBF7EE] tracking-tight mb-4">
            Terms &amp; Conditions
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-[#C9A75E]">
            <span>Karma Resorts Pte Ltd</span>
            <span className="opacity-50">•</span>
            <span>Registration: 200509501G</span>
            <span className="opacity-50">•</span>
            <span>Effective Date: 1 January 2026</span>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1240px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Sticky Sidebar Navigation */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 bg-white border border-[#E5DFD3] rounded-2xl p-6 shadow-sm">
            <div className="font-serif-editorial text-xl font-semibold text-[#0F2340] pb-3 mb-4 border-b border-[#EEE9DC]">
              Table of Contents
            </div>
            <nav className="space-y-1">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollTo(sec.id)}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                    activeSection === sec.id
                      ? 'bg-[#FBF3E1] text-[#967739] font-medium pl-4'
                      : 'text-[#55606E] hover:bg-[#FBF7EE] hover:text-[#0F2340]'
                  }`}
                >
                  <span className="truncate">
                    <span className="text-[#B8934A] font-semibold mr-2">{sec.num}.</span>
                    {sec.title}
                  </span>
                  <ChevronRight size={14} className={`shrink-0 ${activeSection === sec.id ? 'opacity-100' : 'opacity-30'}`} />
                </button>
              ))}
            </nav>

            <div className="mt-6 pt-5 border-t border-[#EEE9DC] text-xs text-[#718096]">
              Need assistance? Contact{' '}
              <a href="mailto:info@karmagroup.com" className="text-[#B8934A] underline font-medium">
                info@karmagroup.com
              </a>
            </div>
          </aside>

          {/* Legal Clauses Card */}
          <main className="lg:col-span-8 bg-white border border-[#E5DFD3] rounded-2xl p-6 md:p-10 shadow-sm leading-relaxed text-[15px]">
            <div className="text-[#0F2340] font-medium text-base md:text-lg pb-6 mb-8 border-b border-[#EEE9DC]">
              Please read these Terms and Conditions carefully before using our website or booking a promotional holiday experience. By accessing or using this site, you agree to be legally bound by these terms.
            </div>

            {/* Section 1 */}
            <section id="section-1" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">1.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Who Are We?</h2>
              </div>
              <p className="mb-4">
                This website is operated by <strong>Karma Resorts Pte Ltd</strong>, a private limited company incorporated under the laws of Singapore with registered company number <strong>200509501G</strong>.
              </p>
              <div className="bg-[#FBF7EE] border-l-4 border-[#B8934A] p-4 rounded-r-lg my-4 text-sm">
                <strong>Registered Office Address:</strong><br />
                Karma Resorts Pte Ltd<br />
                160 Robinson Road, #18-07<br />
                Singapore 068914
              </div>
              <p>
                References in these Terms to &ldquo;Karma&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo; refer to Karma Resorts Pte Ltd and its affiliated group companies. References to &ldquo;you&rdquo; and &ldquo;your&rdquo; refer to the user or browser of this website or the guest purchasing a holiday experience.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">2.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">The Information on the Site</h2>
              </div>
              <p className="mb-4">
                While we endeavour to ensure that information on the Site is correct, complete, and up to date, we make no representations or warranties of any kind, express or implied, regarding its accuracy, adequacy, or completeness.
              </p>
              <p>
                The information is provided for general guidance and promotional purposes only and does not constitute formal legal, travel, or financial advice. Resort photographs, artist impressions, and suite renderings are indicative and for promotional illustration.
              </p>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">3.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Links to Other Sites</h2>
              </div>
              <p className="mb-4">
                The Site may contain hypertext links to third-party websites or services operated by parties outside Karma Group. Such links are provided solely for your convenience. We have no control over the contents or availability of those websites and accept no liability or responsibility for them.
              </p>
              <p>
                Our inclusion of links to external websites does not imply any endorsement or warranty of the material on those websites or any association with their operators.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">4.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Use of the Site</h2>
              </div>
              <p className="mb-3">
                You agree to use the Site only for lawful personal, non-commercial purposes and in a manner that does not infringe the rights of or restrict any other party&rsquo;s use and enjoyment of the Site. You must not:
              </p>
              <ul className="space-y-2 list-none pl-1 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span>Attempt to gain unauthorised access to our servers, databases, payment gateways, or systems;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span>Introduce trojans, viruses, worms, logic bombs, or other malicious technology;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span>Transmit unauthorised or unsolicited advertising, promotional materials, spam, or chain letters;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span>Copy, extract, scrape, or reproduce Site content without prior written permission from Karma Resorts Pte Ltd.</span>
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">5.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Our Liability to You</h2>
              </div>
              <p className="mb-4">
                Nothing in these Terms excludes or limits our liability for death or personal injury arising from our negligence, fraudulent misrepresentation, or any liability that cannot be excluded by applicable law.
              </p>
              <p className="mb-4">
                To the fullest extent permitted by law, Karma Resorts Pte Ltd excludes all representations, warranties, conditions, or guarantees relating to this website and its contents. We shall not be liable for any indirect, special, punitive, incidental, or consequential loss.
              </p>
              <p>
                In all events, our total aggregate liability arising out of or in connection with the use of the Site shall be strictly limited to the amount paid by you for the specific promotional voucher or booking.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">6.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Your Personal Data</h2>
              </div>
              <p className="mb-4">
                We take your privacy seriously and handle all personal data in strict compliance with the UK GDPR, EU GDPR, and Singapore Personal Data Protection Act (PDPA).
              </p>
              <p>
                By using our Site and submitting booking or activation requests, you acknowledge and agree to our processing of personal data as described in our{' '}
                <Link to="/privacy-policy" className="text-[#B8934A] underline font-semibold">
                  Privacy Policy
                </Link>.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">7.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Changes to the Terms</h2>
              </div>
              <p className="mb-4">
                We reserve the right to amend, update, or replace these Terms from time to time. The updated version will be posted directly to this page with an updated Effective Date.
              </p>
              <p>
                Your continued use of the Site or activation of vouchers following published amendments constitutes your acceptance of the revised terms.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">8.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Purchases and Bookings</h2>
              </div>
              <p className="mb-4">
                Special promotional offers, vouchers, holiday certificates, and discounted preview stays are made available subject to the promotional terms specified upon purchase:
              </p>
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-3 p-3 bg-[#FBF7EE] rounded-lg border border-[#EEE9DC]">
                  <CheckCircle2 size={18} className="text-[#B8934A] shrink-0 mt-0.5" />
                  <div>
                    <strong>Eligibility:</strong> Promotional preview stays are designed for couples and families aged 30&ndash;69 who are cohabiting or married and enjoy premium vacations.
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#FBF7EE] rounded-lg border border-[#EEE9DC]">
                  <CheckCircle2 size={18} className="text-[#B8934A] shrink-0 mt-0.5" />
                  <div>
                    <strong>Resort Preview Presentation:</strong> Promotional preview rates require attendance at an informative 90-minute Karma presentation during your stay.
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#FBF7EE] rounded-lg border border-[#EEE9DC]">
                  <CheckCircle2 size={18} className="text-[#B8934A] shrink-0 mt-0.5" />
                  <div>
                    <strong>Validity &amp; Redemption:</strong> Vouchers must be redeemed and travel completed within the validity timeframe indicated on your confirmation documentation.
                  </div>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">9.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Invalidity</h2>
              </div>
              <p>
                If any provision or part-provision of these Terms is found to be illegal, invalid, or unenforceable by a court of competent jurisdiction, that provision shall be modified to the minimum extent necessary to make it valid and enforceable, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            {/* Section 10 */}
            <section id="section-10" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">10.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Governing Law &amp; Jurisdiction</h2>
              </div>
              <p className="mb-4">
                These Terms of Use, their subject matter, and their formation are governed by the laws of Singapore, without prejudice to any mandatory consumer protection rights granted to you by the laws of your country of habitual residence.
              </p>
              <p>
                You and Karma Resorts Pte Ltd agree that the courts of Singapore shall have exclusive jurisdiction to settle any dispute or claim, unless you reside in the United Kingdom or European Union, in which case you may bring claims in your local courts.
              </p>
            </section>

            {/* Section 11 */}
            <section id="section-11" className="scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">11.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Contact Us</h2>
              </div>
              <p className="mb-4">
                If you have any questions, feedback, or complaints regarding the Site or these Terms, please reach out to our guest relations team:
              </p>
              <div className="bg-[#FBF7EE] border border-[#E5DFD3] p-5 rounded-xl text-sm space-y-2">
                <div className="flex items-center gap-2 text-[#0F2340] font-semibold">
                  <Mail size={16} className="text-[#B8934A]" />
                  <span>Email:</span>
                  <a href="mailto:info@karmagroup.com" className="text-[#B8934A] underline font-normal">
                    info@karmagroup.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-[#0F2340] font-semibold">
                  <Globe size={16} className="text-[#B8934A]" />
                  <span>Website:</span>
                  <a href="https://discover.karmaexperience.co.uk/" className="text-[#B8934A] underline font-normal">
                    discover.karmaexperience.co.uk
                  </a>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
